import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import generatePDF, { Options } from "react-to-pdf";
import { authClient } from "../utils/amplifyClient";
import { getUrl } from "aws-amplify/storage";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import { Btn, Card, PageHeader, Stack } from "./ui";

import * as Sentry from "@sentry/browser";
import { ValueWithLabel } from "./ValueWithLabel";
import {
    getDocument as getDocumentQuery,
    getVideo as getVideoQuery
} from "../graphql/queries";
import {
    LOCAL_STORAGE_KEYS,
} from "../utils/constants";

import { PDF_OPTIONS } from "../utils/constants";

import { useAppContext } from "../contexts/AppContext";

export const SafetyReport = () => {
    const reportFromLS = localStorage.getItem(LOCAL_STORAGE_KEYS.SAFETY_REPORT)
    const reportData = reportFromLS ? JSON.parse(reportFromLS) : null;

    const navigate = useNavigate();
    const [report] = useState(reportData);
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [docUrl, setDocUrl] = useState<string | null>(null);
    const [videoUrl, setVideoUrl] = useState<string | null>(null);

    const [signature, setSignature] = useState<string | null>(null);

    const [shouldPrintReport, setShouldPrintReport] = useState(false);
    const { isSideNavOpen, setIsSideNavOpen, initialSideNavOpenState, setInitialSideNavOpenState } = useAppContext();

    const getTargetElement = () => document.getElementById("content-id");

    useEffect(() => {
        getSignature();
        getSafetyContent()

    }, [report])

    useEffect(() => {
        if (shouldPrintReport) {
            window.print();
            setShouldPrintReport(false);
            setIsSideNavOpen(initialSideNavOpenState)
        }
    }, [shouldPrintReport])

    const getSignature = async () => {
        if (!report?.signature) return;
        const signatureUrl = await getUrl({
            path: report.signature,
        });
        setSignature(signatureUrl.url.href);
    };

    const getSafetyContent = async () => {
        if (!report.id) return;

        try {

            if (report.type === "document") {
                const docResponse = await authClient.graphql({
                    query: getDocumentQuery,
                    variables: {
                        id: report.id,
                    },
                })
                if (docResponse.data.getDocument?.contentPath) {
                    const contentPath = await getUrl({
                        path: docResponse.data.getDocument.contentPath,
                    });

                    docResponse.data.getDocument?.contentPath.endsWith('.pdf') ? setPdfUrl(`https://docs.google.com/viewer?url=${encodeURIComponent(contentPath.url.href)}&embedded=true`)
                        : setDocUrl(contentPath.url.href)
                }
            }
            if (report.type === "video") {
                const videoResponse = await authClient.graphql({
                    query: getVideoQuery,
                    variables: {
                        id: report.id,
                    },
                })
                if (videoResponse.data.getVideo?.contentPath) {
                    const contentPath = await getUrl({
                        path: videoResponse.data.getVideo.contentPath,
                    });

                    const videoURL = contentPath.url.href
                    setVideoUrl(videoURL)

                }
            }
        } catch (error) {
            console.error(error);
            Sentry.captureException(error);
        }
    }

    return (
        <div className="page-container safety-report">
            <PageHeader
                as="h2"
                className="hide-from-printer"
                actions={
                    <Stack direction="horizontal" size="l">
                        <Btn
                            variant="icon"
                            ariaLabel="Print"
                            buttonIcon={<PrintIcon />}
                            buttonOnClick={() => {
                                setInitialSideNavOpenState(isSideNavOpen)
                                setIsSideNavOpen(false)
                                setShouldPrintReport(true)
                            }}
                        />
                        <Btn
                            variant="icon"
                            ariaLabel="Download PDF"
                            buttonIcon={<DownloadIcon />}
                            buttonOnClick={() =>
                                generatePDF(getTargetElement, {
                                    filename: `Safety-Report/${report.userName}/${report.dateCompleted}`,
                                    ...PDF_OPTIONS,
                                } as Options)
                            }
                        />
                        <Btn
                            variant="link"
                            buttonText="Back"
                            buttonOnClick={() => {
                                localStorage.removeItem(LOCAL_STORAGE_KEYS.SAFETY_REPORT)
                                navigate(-1)
                            }}
                        />
                    </Stack>
                }
            />

            <Card variant="card-sm" className="hide-from-printer safety-report-layout">
                    <div id="content-id">
                        <Stack size="m">
                            <ValueWithLabel label="Employee:">
                                {report?.userName}
                            </ValueWithLabel>
                            <ValueWithLabel label="Safety Content Title:">
                                {report?.name ? report.name :
                                    report?.signature ? (() => {
                                        const pathParts = report.signature.split('/');
                                        const fullFilename = pathParts[pathParts.length - 1];
                                        const match = fullFilename.match(/-SAFETY-SIGNATURE-(.+)\.jpeg$/);
                                        if (match) {
                                            return match[1];
                                        }
                                        return fullFilename;
                                    })()
                                        : "--"
                                }
                            </ValueWithLabel>
                            <ValueWithLabel label={"Content Type:"}>
                                {report.type}
                            </ValueWithLabel>
                            <ValueWithLabel label={"Date Viewed"}>
                                {report.dateCompleted}
                            </ValueWithLabel>

                            {signature ? <ValueWithLabel label="Signature:">
                                <img style={{ display: 'block', maxHeight: '60px', width: 'auto', objectFit: 'contain' }} src={signature} alt="Signature" crossOrigin="anonymous" />
                            </ValueWithLabel> : <></>}
                        </Stack>
                    </div>
                    <div>

                        {pdfUrl && (
                            <iframe
                                src={pdfUrl}
                                width="100%"
                                height="500px"
                                style={{
                                    border: "none",
                                }}
                            />
                        )}
                        {docUrl && (
                            <img
                                src={docUrl}
                                alt="Document"
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: "500px",
                                    objectFit: "contain"
                                }}
                            />
                        )}
                        {videoUrl && (
                            <video
                                src={videoUrl}
                                controls
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: "500px",
                                    objectFit: "contain"
                                }}
                            />
                        )}
                    </div>
            </Card>
            <div className="viewInPrint">
                <Stack size="m">
                    <ValueWithLabel label="Employee:">
                        {report?.userName}
                    </ValueWithLabel>
                    <ValueWithLabel label="Safety Content Title:">
                        {report?.name ? report.name :
                            report?.signature ? (() => {
                                const pathParts = report.signature.split('/');
                                const fullFilename = pathParts[pathParts.length - 1];
                                const match = fullFilename.match(/-SAFETY-SIGNATURE-(.+)\.jpeg$/);
                                if (match) {
                                    return match[1];
                                }
                                return fullFilename;
                            })()
                                : "--"
                        }
                    </ValueWithLabel>
                    <ValueWithLabel label={"Content Type:"}>
                        {report.type}
                    </ValueWithLabel>
                    <ValueWithLabel label={"Date Viewed"}>
                        {report.dateCompleted}
                    </ValueWithLabel>

                    {signature ? <ValueWithLabel label="Signature:">
                        <img style={{ display: 'block', maxHeight: '60px', width: 'auto', objectFit: 'contain' }} src={signature} alt="Signature" />
                    </ValueWithLabel> : <></>}
                </Stack>
            </div>

        </div>

    );
};
